import React, { useState } from "react";
import { DatePicker, Space, Button, Table, Typography, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import {
  useInventoryReportsServiceGetEmployeeInvoiceSummary,
  useInventoryReportsServiceDownloadEmployeeInvoiceSummary,
} from "api/queries";

const { Title } = Typography;

const SalesByEmployeeReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");

  const { data, isLoading } =
    useInventoryReportsServiceGetEmployeeInvoiceSummary({
      startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    });

  const { mutate: getDownloadUrl, isPending } =
    useInventoryReportsServiceDownloadEmployeeInvoiceSummary();

  const employeeSalesData =
    data?.employees?.map((item: any, idx: number) => ({
      key: item.employeeId || idx,
      employeeName: item.employeeName,
      invoicesHandled: item.invoiceCount,
      totalRevenue: item.totalRevenue,
      averageInvoiceValue: item.averageInvoiceValue,
    })) || [];

  const columns = [
    { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
    {
      title: "Invoices Handled",
      dataIndex: "invoicesHandled",
      key: "invoicesHandled",
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: "Average Invoice Value",
      dataIndex: "averageInvoiceValue",
      key: "averageInvoiceValue",
      render: (value: number) => value?.toLocaleString(),
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
  };

  const handleDownload = () => {
    getDownloadUrl(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: {
          success: boolean;
          message: string;
          downloadUrl?: string;
        }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "sales_by_employee_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Sales by Employee Report</Title>
          <Space>
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              <Select.Option value="Today">Today</Select.Option>
              <Select.Option value="This Week">This Week</Select.Option>
              <Select.Option value="This Month">This Month</Select.Option>
              <Select.Option value="This Year">This Year</Select.Option>
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={employeeSalesData}
          pagination={{ pageSize: 10 }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
        />
      </Space>
    </motion.div>
  );
};

export default SalesByEmployeeReport;
